/**
 * Hotel Profile package row — headline ₪ מ־ourPrice (PWCCF) + דולר SSOT בלבד.
 * תואם ל־services/hotel_package_display_currencies.py: אין המרת ILS→USD בצד לקוח.
 *
 * @typedef {{
 *   ils: number,
 *   usd: number|null,
 *   hasUsd: boolean,
 *   formattedIls: string,
 *   formattedUsd: string,
 *   usdSource: string|null
 * }} DisplayCurrencies
 */

import {
  extractAuthoritativeUsdWithSource,
  mapRoomDisplayPriceToDisplayCurrencies,
  resolveHotelRoomPackageDisplayPrice,
} from "./resolveHotelRoomPackageDisplayPrice";
import { formatHotelSearchAmountForDisplay } from "../constants/hotelCurrencies";
import {
  formatHotelIlsRoundedDisplay,
  formatHotelIlsRoundedDisplayFromDisplayCurrencies,
} from "./formatHotelIlsRoundedDisplay";

export { extractAuthoritativeUsdWithSource } from "./resolveHotelRoomPackageDisplayPrice";

function positiveFloat(v) {
  if (v === null || v === undefined) return null;
  const x = typeof v === "number" ? v : parseFloat(String(v));
  if (!Number.isFinite(x) || x <= 0) return null;
  return x;
}

function extractDisplayIls(pkg) {
  const op = pkg?.ourPrice;
  if (op && typeof op === "object") {
    const v = positiveFloat(op.priceWithCommissionAndCreditCardFee);
    if (v !== null) return v;
  }
  return 0;
}

function fmtGroupedNumber(n, maxDecimals) {
  if (maxDecimals <= 0) {
    return Math.round(n).toLocaleString("en-US");
  }
  const rounded = Number(Number(n).toFixed(maxDecimals));
  if (Math.abs(rounded - Math.round(rounded)) < 1e-9) {
    return Math.round(rounded).toLocaleString("en-US");
  }
  return rounded.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

function computeHotelPackageDisplayCurrencies(pkg) {
  const ils = extractDisplayIls(pkg);
  const { usd, usdSource } = extractAuthoritativeUsdWithSource(pkg);
  const hasUsd = usd !== null;

  const formattedUsd =
    hasUsd && usd !== null ? `($${fmtGroupedNumber(usd, 2)})` : "";
  const formattedIls =
    ils > 0
      ? formatHotelIlsRoundedDisplay(ils)
      : hasUsd && usd != null
        ? formatHotelSearchAmountForDisplay(usd, "USD") ?? `($${fmtGroupedNumber(usd, 2)})`
        : "₪ 0";

  return {
    ils,
    usd,
    hasUsd,
    formattedIls,
    formattedUsd,
    usdSource: hasUsd ? usdSource : null,
  };
}

function serverDisplayCurrenciesNeedsFxFallback(dc, profilePriceContext) {
  if (!profilePriceContext || !dc || typeof dc !== "object") return false;
  const ilsNum = Number(dc.ils);
  const usdNum =
    dc.usd != null && Number.isFinite(Number(dc.usd)) ? Number(dc.usd) : null;
  const hasPositiveUsd =
    (usdNum != null && usdNum > 0) ||
    dc.hasUsd === true ||
    (typeof dc.formattedUsd === "string" && String(dc.formattedUsd).trim() !== "");
  return (!Number.isFinite(ilsNum) || ilsNum <= 0) && hasPositiveUsd;
}

/**
 * נקודת כניסה יחידה לטבלת החדרים בפרופיל.
 * אם השרת שלח `displayCurrencies` (לאחר createBoxes) — משתמשים בו כדי להימנע מפיצול עיגולים.
 * כש-ILS חסר/0 ו-USD תקף — ממיר דרך profile usdToIls (אותו SSOT כמו שורת חדר).
 *
 * @param {Record<string, unknown>|null|undefined} pkg
 * @param {object|null|undefined} [profilePriceContext]
 * @returns {DisplayCurrencies}
 */
export function resolveHotelPackageDisplayCurrencies(pkg, profilePriceContext = null) {
  const dc = pkg?.displayCurrencies;
  if (
    dc &&
    typeof dc === "object" &&
    typeof dc.formattedIls === "string" &&
    !serverDisplayCurrenciesNeedsFxFallback(dc, profilePriceContext)
  ) {
    const ilsNum = Number(dc.ils);
    const usdNum =
      dc.usd != null && Number.isFinite(Number(dc.usd))
        ? Number(dc.usd)
        : null;
    const hasUsd = Boolean(dc.hasUsd);
    const apiUsdSource =
      typeof dc.usdSource === "string" && dc.usdSource.trim() !== ""
        ? dc.usdSource
        : hasUsd
          ? "hotel_information.display_currencies"
          : null;
    return {
      ils: Number.isFinite(ilsNum) && ilsNum > 0 ? ilsNum : 0,
      usd: usdNum,
      hasUsd,
      formattedIls:
        formatHotelIlsRoundedDisplayFromDisplayCurrencies(dc) ?? dc.formattedIls,
      formattedUsd: typeof dc.formattedUsd === "string" ? dc.formattedUsd : "",
      usdSource: hasUsd ? apiUsdSource : null,
    };
  }

  if (profilePriceContext) {
    const ssot = resolveHotelRoomPackageDisplayPrice(pkg, profilePriceContext);
    return mapRoomDisplayPriceToDisplayCurrencies(ssot);
  }

  return computeHotelPackageDisplayCurrencies(pkg || {});
}
